import { useEffect, useState } from "react";
import { Button, InlineAlert, LoadingSpinner } from "../../components/BaseComponents";
import CanonicalModal from "../../components/BaseComponents/CanonicalModal/CanonicalModal";
import SimpleSortTable, { TableColumn } from "../../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import useApi from "../../hooks/useApi";
import useCleanup from "../../hooks/useCleanup";
import { getApiErrorMessage } from "../../lib/utils";
import EditTrainingOrganisationModal from "./EditTrainingOrganisationModal";

type ManageTrainingOrganisationsModalProps = {
	trainingSessionId: string;
	isOpen?: boolean;
	onClose: () => void;
};

// Sub-task I-extra2 §4.2/§8.3/§16.3 (docs/todo/user-org-membership-i-extra2-training-org-reassignment.md):
// "what is this org" as its own list+edit flow, kept separate from reassigning which trainees
// belong to it (EditTrainingUserModal's reassignment combo-box).
function ManageTrainingOrganisationsModal({ trainingSessionId, isOpen, onClose }: ManageTrainingOrganisationsModalProps) {
	const [organisations, setOrganisations] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState('');
	const [editCandidate, setEditCandidate] = useState<any>(null);
	const api = useApi();

	const loadOrganisations = useCleanup(() => {
		if (!isOpen) {
			return;
		}
		let cancelled = false;
		const run = async () => {
			setLoading(true);
			setLoadError('');
			try {
				const result = await api.get(`admin/training-sessions/${trainingSessionId}/organisations`);
				if (cancelled) {
					return;
				}
				setOrganisations(result.data.organisations || []);
			} catch (err) {
				if (!cancelled) {
					setLoadError(getApiErrorMessage(err, 'Could not load organisations. Please try again.'));
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		}
		run();
		return () => {
			cancelled = true;
		}
	})

	useEffect(() => {
		loadOrganisations();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [trainingSessionId, isOpen]);

	const columns: TableColumn[] = [{
		id: 'name',
		label: 'Organisation',
		// §16.3: jurisdiction is shown appended to the name, e.g. "Acme Training (DE)" -
		// consistent with the same treatment on the training-session users table.
		renderer: (key, row) => <span>{row[key]}<span className="text-charcoal-300"> ({row.jurisdictionCode})</span></span>,
	}, {
		id: 'memberCount',
		label: 'Members',
	}, {
		id: 'pendingCount',
		label: 'Pending',
	}, {
		id: 'actions',
		label: 'Actions',
		headerAlign: 'center',
		textAlign: 'right',
		renderer: (key, row) => (
			<Button
				type="Primary"
				icon="fa-solid fa-pen-to-square"
				hasLeadingIcon
				size="sm"
				isTableRowButton
				onClick={() => setEditCandidate(row)}
				accessibilityLabel={`Edit ${row.name}`}
			/>
		),
	}]

	return (
		<CanonicalModal
			open={!!isOpen}
			onClose={onClose}
			size="large"
			title="Manage organisations"
			subtitle="Rename an organisation or change its jurisdiction"
			actions={<Button onClick={onClose} type="Primary" text="Close" />}
		>
			{ loading && <LoadingSpinner /> }
			{loadError ? (
				<InlineAlert type='error' className='mt-4'>
					<p>{loadError}</p>
					<Button type='Primary' text='Retry' onClick={loadOrganisations} />
				</InlineAlert>
			) : (
				<SimpleSortTable
					additionalClass="mt-4"
					columns={columns}
					data={organisations}
					isLoading={loading}
				/>
			)}
			<EditTrainingOrganisationModal
				isOpen={!!editCandidate}
				trainingSessionId={trainingSessionId}
				organisation={editCandidate}
				onClose={() => setEditCandidate(null)}
				onUpdated={() => {
					setEditCandidate(null);
					loadOrganisations();
				}}
			/>
		</CanonicalModal>
	)
}

export default ManageTrainingOrganisationsModal
